import React, { useState } from "react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, Undo, ArrowUpDown } from "lucide-react";
import { cn, displayName } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TicketSortField, SortDirection } from "@/hooks/useAdminTickets";
import { displayAdminEventLabel } from "@/lib/datetime";

interface Ticket {
  id: string;
  created_at: string;
  total_price: number;
  actual_amount_paid: number | null;
  credits_spent: number | null;
  quantity: number;
  status: string;
  payment_method: string;
  refunded_by: string | null;
  refunded_at: string | null;
  events?: {
    title: string;
    start_time: string;
    timezone?: string | null;
  };
  profiles?: {
    id: string;
    first_name: string;
    email: string;
    phone: string;
    last_name: string;
  };
  refunder?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
}

interface TicketsTableProps {
  tickets?: Ticket[];
  isLoading: boolean;
  isEmpty: boolean;
  searchQuery: string;
  deleteTicket: (id: string) => Promise<void>;
  refundTicket: (ticketId: string, eventTitle: string, userName: string) => Promise<void>;
  refundingTicketId: string | null;
  sortField: TicketSortField;
  sortDirection: SortDirection;
  onSort: (field: TicketSortField) => void;
}

const TicketsTable: React.FC<TicketsTableProps> = ({
  tickets,
  isLoading,
  isEmpty,
  searchQuery,
  deleteTicket,
  refundTicket,
  refundingTicketId,
  sortField,
  sortDirection,
  onSort
}) => {
  const [selectedGuest, setSelectedGuest] = useState<Ticket["profiles"] | null>(null);

  const SortableHeader = ({ 
    field, 
    children, 
    className 
  }: { 
    field: TicketSortField; 
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead 
      className={cn("cursor-pointer hover:bg-muted/50 transition-colors", className)}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className={cn(
          "h-3 w-3",
          sortField === field ? "text-secondary" : "text-muted-foreground"
        )} />
      </div>
    </TableHead>
  );

  return (
    <>
      <Dialog open={!!selectedGuest} onOpenChange={(open) => !open && setSelectedGuest(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Guest Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Name</span>
              <span className="font-medium">
                {displayName(selectedGuest?.first_name, selectedGuest?.last_name)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Email</span>
              <span className="font-medium">{selectedGuest?.email || "N/A"}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Phone</span>
              <span className="font-medium">{selectedGuest?.phone || "N/A"}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">User ID</span>
              <span className="font-mono text-xs">{selectedGuest?.id}</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Table>
        <TableHeader>
          <TableRow className="bg-muted">
            <SortableHeader field="event">Event</SortableHeader>
            <SortableHeader field="created_at" className="text-right">Booked Date</SortableHeader>
            <SortableHeader field="guest" className="text-left">Guest</SortableHeader>
            <SortableHeader field="quantity" className="text-center">Quantity</SortableHeader>
            <SortableHeader field="total_price" className="text-right">Total Price</SortableHeader>
            <TableHead className="text-right">Amount Paid</TableHead>
            <TableHead className="text-right">Credits Used</TableHead>
            <SortableHeader field="status" className="text-center">Status</SortableHeader>
            <TableHead className="text-center">
              Payment
              <br />
              Method
            </TableHead>
            <TableHead className="text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tickets?.map((ticket) => (
            <TableRow key={ticket.id} className="h-9">
              <TableCell className="font-medium py-1">
                {ticket.events
                  ? displayAdminEventLabel(ticket.events.title, ticket.events.start_time, ticket.events.timezone || undefined)
                  : "Unknown Event"}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap py-1">
                {format(new Date(ticket.created_at), "M/d/yyyy, h:mm:ss a")}
              </TableCell>
              <TableCell className="text-left whitespace-nowrap py-1">
                <button
                  className="font-medium text-primary hover:underline cursor-pointer text-left"
                  onClick={() => setSelectedGuest(ticket.profiles)}
                >
                  {displayName(ticket.profiles?.first_name, ticket.profiles?.last_name)}
                </button>
              </TableCell>
              <TableCell className="text-center whitespace-nowrap py-1">{ticket.quantity}</TableCell>
              <TableCell className="text-right whitespace-nowrap py-1">
                £{ticket.total_price}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap py-1">
                {ticket.actual_amount_paid ? `£${ticket.actual_amount_paid}` : '-'}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap py-1">
                {ticket.credits_spent || '-'}
              </TableCell>
              <TableCell
                className={cn(
                  "text-center font-medium whitespace-nowrap py-1",
                  ticket.status === "confirmed"
                    ? "text-success"
                    : ticket.status === "refunded"
                    ? "text-muted-foreground"
                    : "text-warning"
                )}
              >
                <div className="flex flex-col items-center gap-0.5">
                  <span>{ticket.status}</span>
                  {ticket.status === "refunded" && (
                    <span className="text-[10px] font-normal text-muted-foreground">
                      {ticket.refunded_by
                        ? `by ${ticket.refunder?.first_name || "admin"}`
                        : "System (no-show auto-refund)"}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-center font-medium whitespace-nowrap py-1">
                {ticket.payment_method}
              </TableCell>
              <TableCell className="whitespace-nowrap py-1">
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    className="text-info hover:text-info/80 disabled:opacity-50 h-7 w-7"
                    size="icon"
                    onClick={() => refundTicket(
                      ticket.id,
                      ticket.events
                        ? displayAdminEventLabel(ticket.events.title, ticket.events.start_time, ticket.events.timezone || undefined)
                        : 'Unknown Event',
                      displayName(ticket.profiles?.first_name, ticket.profiles?.last_name)
                    )}
                    disabled={ticket.status === 'refunded' || refundingTicketId === ticket.id}
                    title={ticket.status === 'refunded' ? 'Already refunded' : 'Process refund'}
                  >
                    {refundingTicketId === ticket.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Undo className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    className="text-destructive hover:text-destructive/80 h-7 w-7"
                    size="icon"
                    onClick={() => deleteTicket(ticket.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {/* loading state */}
          {isLoading && (
            <TableRow>
              <TableCell colSpan={10} className="text-center text-muted-foreground">
                Loading tickets...
              </TableCell>
            </TableRow>
          )}

          {/* empty state */}
          {isEmpty && (
            <TableRow>
              <TableCell colSpan={10} className="text-center text-muted-foreground">
                No tickets found{" "}
                {searchQuery ? `for event "${searchQuery}"` : ""}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};

export default TicketsTable;
